import React, { Fragment, useEffect, useState } from "react";
import { DefaultEventTypeIconModel, EventTypeCustomIconSymbols } from "pages/fleet/fleet-event-type/wizard/shared/DefaultEventTypeModel";
import { ShowHide } from "components/layout";
import { AlertUnsavedData, NotificationToaster } from "components/notifications";
import { UserAccess, UserHasAccess } from "components/user-access";
import { useDirtyData } from "hooks/useDirtyData";
import { useValidation } from "hooks/useValidation";
import { PageRestricted } from "pages/errors/page-restricted";
import PropTypes from "prop-types";
import { GridLayout } from "components/grid";
import { FormSelect } from "components/form-fields";
import { FleetService } from "services";
import { Intent } from "@blueprintjs/core";
import "./EventTypeIcon.css";
import { ButtonSave } from "components/buttons";
import { useNavigate } from "react-router-dom";

export function EventTypeIcon({
    model = DefaultEventTypeIconModel,
    forceLoad = false,
    isInWizardMode = true,
    saving = false,
    globalLoading = false,
    onValidationUpdate = () => { },
    onModelUpdate = () => { return DefaultEventTypeIconModel; },
    onForceLoadFinished = () => { },
    onSave = () => { }
}) {
    var navigate = useNavigate();

    const step = 1;
    const fmsManageAction = "FMS:Manage";
    const userHasFmsManageAction = UserHasAccess(fmsManageAction);

    const [loading, setLoading] = useState(true);
    const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);
    const [optionsFetched, setOptionsFetched] = useState(false);

    const [originalIconModel, setOriginalIconModel] = useState(DefaultEventTypeIconModel);
    const [iconModel, setIconModel] = useState(DefaultEventTypeIconModel)
    const eventIconNameFieldName = "eventIconName";
    const eventIconFieldName = "eventIcon";
    const iconShapeFieldName = "iconShape";
    const iconSymbolFieldName = "iconSymbol";

    const [isValid, iconModelErrors, validateIconModel] = useValidation();
    const isIconModelDirty = useDirtyData(originalIconModel, iconModel);

    const customIconFieldName = "customIcon";
    const customEventIcon = { key: "custom", id: -1, name: "Custom" };
    const [eventIcons, setEventIcons] = useState([customEventIcon]);
    const [iconShapes, setIconShapes] = useState([]);
    const iconSymbols = EventTypeCustomIconSymbols;

    const emptyEventIconModel = { key: null, id: null, name: null };
    const emptyEventSymbolModel = { id: null, key: null, name: null };

    const [selectedEventIcon, setSelectedEventIcon] = useState(emptyEventIconModel);
    const [selectedShape, setSelectedShape] = useState(emptyEventIconModel);
    const [selectedSymbol, setSelectedSymbol] = useState(emptyEventSymbolModel);

    useEffect(() => {
        if (!userHasFmsManageAction || (forceLoad === false && initialLoadCompleted)) {
            return;
        }
        
        setModel(prev => { return {...prev, ...model}; });

        if (!isInWizardMode && model.eventIconName !== DefaultEventTypeIconModel.eventIconName) {
            setOriginalIconModel(model);
        } 
        
        setInitialLoadCompleted(true);

        fetchOptions(model);

        if (forceLoad) {
            onForceLoadFinished();
        }
    }, [model, forceLoad]);

    function fetchOptions(internalModel) {
        if (optionsFetched) {
            return;
        }

        setLoading(true);

        FleetService.getEventOptions()
            .then((response) => {
                setOptionsFetched(true);
                const tempEventIcons = [customEventIcon, ...response.eventIcons];

                setEventIcons(tempEventIcons);
                setIconShapes(response.eventCustomIconShapes);
                
                setSelectedIconValues(response, internalModel);
            })
            .catch(() => {
                NotificationToaster.show(Intent.WARNING, 'Event options could not be loaded. Please try again later.');
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function setSelectedIconValues(response, internalModel) {
        if (internalModel === null || internalModel === undefined || internalModel.eventIconName === null || internalModel.eventIconName === undefined) {
            return;
        }

        let tempIconSymbolKey = null;
        let tempIconShapeKey = null;

        if (internalModel?.eventIconName !== null && internalModel?.eventIconName !== undefined && internalModel?.eventIconName !== "") {
            tempIconSymbolKey = internalModel.eventIconName.substring(0, 1);
            tempIconShapeKey = internalModel.eventIconName.substring(1);
        } else {
            return;
        }

        // if the eventIconName field has a value and we can find a match in the options then we use that else we assume that its custom
        const eventIcon = response.eventIcons.some(icon => icon.key === internalModel.eventIconName)
            ? response.eventIcons.find(icon => icon.key === internalModel.eventIconName) 
            : customEventIcon;

        // get selected customIcon.iconShape based on the internalModels eventIconName property
        const iconShape = tempIconShapeKey !== null && tempIconShapeKey !== undefined
            ? response.eventCustomIconShapes.find(shape => shape.key === tempIconShapeKey)
            : emptyEventIconModel;

        // get selected customIcon.iconSymbol based on the internalModels eventIconName property
        const iconSymbol = tempIconSymbolKey !== null && tempIconSymbolKey !== undefined
            ? iconSymbols.find(symbol => symbol.key === tempIconSymbolKey)
            : emptyEventSymbolModel;


        const clonedModel = {
            ...internalModel,
            [eventIconFieldName]: eventIcon.id
        }

        if (eventIcon.id === customEventIcon.id) {
            const clonedCustomIconModel = { ...clonedModel[customIconFieldName] };

            if (iconShape !== emptyEventIconModel) {
                clonedCustomIconModel[iconShapeFieldName] = iconShape.id;
            }

            if (iconSymbol !== emptyEventSymbolModel) {
                clonedCustomIconModel[iconSymbolFieldName] = iconSymbol.id;
            }

            clonedModel[customIconFieldName] = clonedCustomIconModel;
        }

        setModel(clonedModel);

        setSelectedEventIcon(eventIcon);
        setSelectedShape(iconShape);
        setSelectedSymbol(iconSymbol);
    }

    useEffect(() => {
        setLoading(globalLoading);
    }, [globalLoading]);

    // publish out validation changes if in wizard mode
    useEffect(() => {
        if (!isInWizardMode) {
            return;
        }

        onValidationUpdate(step, isValid);
    }, [isValid])

    useEffect(validate, [iconModel])

    function validate() {
        let validationRules = [
            { fieldName: eventIconFieldName, required: true }
        ];

        if (iconModel.eventIcon === customEventIcon.id) { 
            validationRules.push({ fieldName: `customIcon.${iconShapeFieldName}`, required: true });
            validationRules.push({ fieldName: `customIcon.${iconSymbolFieldName}`, required: true });
        }
        
        validateIconModel(validationRules, iconModel);
    }

    // Use this to handle input value changes
    function onValueChange(fieldName, item, isCustomIconField = false) {
        const clonedModel = { ...iconModel };

        if (isCustomIconField) {
            const clonedCustomIconModel = { 
                ...clonedModel[customIconFieldName],
                [fieldName]: item.id
            };

            clonedModel[customIconFieldName] = clonedCustomIconModel;
        } else {
            clonedModel[fieldName] = item.id;
        }

        if (fieldName === eventIconFieldName) {
            setSelectedEventIcon(item);

            if (item.id !== customEventIcon.id) {
                clonedModel[eventIconNameFieldName] = item.key;
            }
        }

        switch (fieldName) {
            case eventIconFieldName:
                setSelectedEventIcon(item);
                break;
            case iconShapeFieldName:
                setSelectedShape(item);
                break;
            case iconSymbolFieldName:
                setSelectedSymbol(item);
                break;
            default:
                break;
        }

        setIconModel(clonedModel);
        onModelUpdate(step, clonedModel);
    }

    // try construct the event icon name if the current select eventIcon is custom
    useEffect(() => {
        if (selectedEventIcon?.id !== customEventIcon.id || !hasValidCustomIconSelections()) {
            return;
        }
        
        const iconName = `${selectedSymbol.key}${selectedShape.key.toLowerCase()}`;

        const clonedModel = { 
            ...iconModel, 
            [eventIconNameFieldName]: iconName
        };

        setIconModel(clonedModel);
        onModelUpdate(step, clonedModel);
    }, [selectedEventIcon, selectedShape, selectedSymbol])


    function setModel(changeModel) {
        setIconModel(changeModel);

        // only set the original model if we aren't in wizard mode (basically not in create mode)
        if (!isInWizardMode) {
            setOriginalIconModel(changeModel);
        }
    }

    function showIconPreview() {
        return iconModel.eventIcon !== null && iconModel.eventIcon !== undefined && iconModel.eventIcon !== customEventIcon.id || hasValidCustomIcon();
    }

    function hasValidCustomIcon() {
        return iconModel.eventIcon !== null &&
            iconModel.eventIcon !== undefined &&
            iconModel.eventIcon === customEventIcon.id &&
            iconModel.customIcon.iconShape !== null &&
            iconModel.customIcon.iconShape !== undefined &&
            iconModel.customIcon.iconShape !== 0 &&
            iconModel.customIcon.iconSymbol !== null &&
            iconModel.customIcon.iconSymbol !== undefined && 
            iconModel.customIcon.iconSymbol !== 0;
    }

    function hasValidCustomIconSelections() {
        return selectedEventIcon !== null &&
            selectedEventIcon !== undefined &&
            selectedEventIcon?.id === customEventIcon.id &&
            selectedShape?.id !== null &&
            selectedShape?.id !== undefined &&
            selectedShape?.id !== 0 &&
            selectedSymbol?.id !== null &&
            selectedSymbol?.id !== undefined && 
            selectedSymbol?.id !== 0;
    }

    function showCustomIconFields() {
        return selectedEventIcon.id === customEventIcon.id;
    }

    function onConfirmLeave(redirectToPath) {
        setModel(originalIconModel);
        onModelUpdate(step, originalIconModel);

        setTimeout(() => navigate(redirectToPath));
    }

    return (
        <UserAccess perform={fmsManageAction}
            yes={() => (
                <Fragment>
                    <ShowHide
                        evaluator={!isInWizardMode}
                        show={(
                            <Fragment>
                                <h2>Event Icon</h2>
                                <AlertUnsavedData isDirty={isIconModelDirty} onConfirmLeave={onConfirmLeave} />
                            </Fragment>
                        )}
                    />

                    <GridLayout numberOfColumns={1} className="spacer-bottom">
                        <FormSelect
                            disabled={saving || loading}
                            loading={loading}
                            items={eventIcons}
                            onItemSelect={(item) => onValueChange("eventIcon", item)}
                            placeholder="Please select"
                            headingText="Please select an icon to represent this event type in the Asset page"
                            selectedValue={iconModel?.eventIcon}
                            dangerHelperText={iconModelErrors["eventIcon"]}
                            id="select-event-icon"
                        />
                    </GridLayout>

                    <ShowHide
                        evaluator={showCustomIconFields()}
                        show={(
                            <Fragment>
                                <GridLayout numberOfColumns={3} className="spacer-bottom">
                                    <FormSelect
                                        disabled={saving || loading}
                                        loading={loading}
                                        items={iconShapes}
                                        onItemSelect={(item) => onValueChange("iconShape", item, true)}
                                        placeholder="Please select"
                                        headingText="Icon shape"
                                        selectedValue={iconModel?.customIcon?.iconShape}
                                        dangerHelperText={iconModelErrors["customIcon.iconShape"]}
                                        id="select-event-icon-shape"
                                    />

                                    <FormSelect
                                        disabled={saving || loading}
                                        loading={loading}
                                        items={iconSymbols}
                                        onItemSelect={(item) => onValueChange("iconSymbol", item, true)}
                                        placeholder="Please select"
                                        headingText="Icon symbol"
                                        selectedValue={iconModel?.customIcon?.iconSymbol}
                                        dangerHelperText={iconModelErrors["customIcon.iconSymbol"]}
                                        id="select-event-icon-symbol"
                                    />
                                </GridLayout>
                            </Fragment>
                        )}
                    />

                    <h4>Preview</h4>
                    <div className="icon-container inline-items inline-items-center spacer-bottom">
                        <ShowHide
                            evaluator={showIconPreview()}
                            show={(
                                <i className={`custom-icon custom-icon-${iconModel?.eventIconName}`}/>
                            )}
                        />
                    </div>

                    <ShowHide
                        evaluator={!isInWizardMode}
                        show={(
                            <ButtonSave
                                loading={loading}
                                onClick={() => onSave("icon")}
                                disabled={saving}
                                simpleDisabled={!isIconModelDirty || !isValid}
                            />	
                        )}
                    />
                </Fragment>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

EventTypeIcon.propTypes = {
    model: PropTypes.object,
    forceLoad: PropTypes.bool,
    isInWizardMode: PropTypes.bool,
    saving: PropTypes.bool.isRequired,
    globalLoading: PropTypes.bool,
    onValidationUpdate: PropTypes.func,
    onModelUpdate: PropTypes.func,
    onSave: PropTypes.func,
    onForceLoadFinished: PropTypes.func,
};